import { supabase } from '../lib/supabase';
import { supabaseAdmin, isAdminAvailable } from '../lib/supabaseAdmin';
import { mapStudentStatusToProfileStatus } from '../utils/accountStatus';

const isMissingScopedStudentsRpc = (error) =>
  error && (error.code === 'PGRST202' || /Could not find the function public\.get_scoped_students/i.test(error.message || ''));

const canUseAdministrativeFallback = async () => {
  const { data: authData } = await supabase.auth.getUser();
  if (!authData?.user?.id) return false;
  const { data: profile } = await supabase.from('user_profiles').select('primary_role').eq('id', authData.user.id).maybeSingle();
  return ['system_administrator', 'chief_principal', 'vice_chancellor', 'vc', 'principal', 'deputy_principal_academic', 'registrar_academic'].includes(profile?.primary_role);
};

export const studentService = {
  // ---------------------------------------------------------------------------
  // 1. READ Operations
  // ---------------------------------------------------------------------------

  getStudentBilling: async (studentId) => {
    try {
      const { data, error } = await supabase
        .from('student_billing_requests')
        .select('*, academic_year:academic_year_id(name), semester:semester_id(name)')
        .eq('student_id', studentId)
        .order('requested_at', { ascending: false });
        
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error fetching billing data:', error);
      return { data: null, error };
    }
  },

  getStudentStatement: async (studentId) => {
    try {
      const { data, error } = await supabase.rpc('get_student_statement', { p_student_id: studentId });
      if (!error) return { data, error: null };

      // Some existing databases have the student_ledger table but are missing
      // the statement RPC. Read the student's own ledger through RLS so the
      // fee statement still shows every charge (including exam bookings) while
      // the RPC migration is being applied.
      const { data: ledger, error: ledgerError } = await supabase
        .from('student_ledger')
        .select('id,transaction_date,doc_no,description,transaction_type,debit_amount,credit_amount,created_at')
        .order('transaction_date', { ascending: true })
        .order('created_at', { ascending: true });
      if (ledgerError) throw ledgerError;

      let runningBalance = 0;
      const statementRows = (ledger || []).map(row => {
        runningBalance += (Number(row.debit_amount) || 0) - (Number(row.credit_amount) || 0);
        return { ...row, running_balance: runningBalance };
      });
      return { data: statementRows, error: null };
    } catch (error) {
      console.error('Error fetching student statement:', error);
      return { data: null, error };
    }
  },

  /**
   * Fetch all students with advanced filtering
   */
  getStudents: async (filters = {}) => {
    try {
      console.log('🔍 Fetching students filtered by:', filters);

      const { data: scopedRows, error: scopedError } = await supabase.rpc('get_scoped_students');
      let scoped;
      if (scopedError) {
        if (!isMissingScopedStudentsRpc(scopedError) || !(await canUseAdministrativeFallback())) throw scopedError;
        const fallback = await supabase.from('students').select('*, program:program_id(id,name,code,department_id,section_id)');
        if (fallback.error) throw fallback.error;
        scoped = fallback.data || [];
      } else {
        scoped = (scopedRows || []).map(row => row.student || row);
      }

      // Apply Filters
      if (filters.departmentId) scoped = scoped.filter(row => String(row.program?.department_id || row.department_id) === String(filters.departmentId));
      if (filters.programId) scoped = scoped.filter(row => String(row.program_id) === String(filters.programId));
      if (filters.academicLevel) scoped = scoped.filter(row => String(row.academic_level) === String(filters.academicLevel));
      if (filters.intake) scoped = scoped.filter(row => row.intake_code === filters.intake);
      if (filters.status) scoped = scoped.filter(row => String(row.status).toLowerCase() === String(filters.status).toLowerCase());
      if (filters.search) {
        const term=filters.search.toLowerCase();scoped=scoped.filter(row=>[row.admission_number,row.email,row.institutional_email,row.personal_email,row.first_name,row.last_name,row.full_name].some(value=>String(value||'').toLowerCase().includes(term)));
      }

      console.log('🔍 Executing query...');
      const data = scoped.sort((a,b)=>String(b.created_at||'').localeCompare(String(a.created_at||'')));
      const error = null;

      console.log('✅ Query complete');
      console.log('   - Data:', data);
      console.log('   - Count:', data?.length || 0);
      console.log('   - Error:', error);

      if (error) {
        console.error('❌ Query error:', error);
        throw error;
      }

      console.log('📊 Returning', data?.length || 0, 'students');
      return data || [];
    } catch (error) {
      console.error('❌ Error in studentService.getStudents:', error);
      return [];
    }
  },

  /**
   * Get filtered student counts for dashboard
   */
  getStudentStats: async () => {
    try {
      const { data, error } = await supabase
        .from('students')
        .select('status, program_id');

      if (error) throw error;

      const stats = {
        total: data.length,
        active: data.filter(s => s.status === 'Active').length,
        pending: data.filter(s => s.status === 'Pending').length,
        graduated: data.filter(s => s.status === 'Graduated').length,
        inactive: data.filter(s => s.status === 'Inactive').length,
        byProgram: {}
      };

      data.forEach(s => {
        if (s.program_id) {
          stats.byProgram[s.program_id] = (stats.byProgram[s.program_id] || 0) + 1;
        }
      });

      return stats;
    } catch (error) {
      console.error('Error fetching student stats:', error);
      return { total: 0, active: 0, pending: 0 };
    }
  },

  /**
   * Get single student by ID (profile_id)
   */
  getStudentById: async (id) => {
    try {
      // The list and detail pages must use the same organisational scope. A
      // direct students query can be rejected by RLS for an HOD even though the
      // scoped RPC correctly includes the learner.
      const { data: scopedRows, error: scopedError } = await supabase.rpc('get_scoped_students');
      if (scopedError) throw scopedError;
      const scopedStudent = (scopedRows || [])
        .map(row => row.student || row)
        .find(row => String(row.profile_id || row.id) === String(id));
      if (!scopedStudent) return null;

      let program = scopedStudent.program || null;
      if (!program && scopedStudent.program_id) {
        const { data: programData } = await supabase
          .from('programs')
          .select('id,name,code,department_id,section_id')
          .eq('id', scopedStudent.program_id)
          .maybeSingle();
        program = programData || null;
      }

      const { data: documents, error: documentError } = await supabase
        .from('student_documents')
        .select('*')
        .eq('student_id', id)
        .order('created_at', { ascending: false });
      if (documentError) console.warn('Student documents are not available to this role:', documentError);

      return { ...scopedStudent, program, documents: documents || [] };
    } catch (error) {
      console.error('Error fetching student details:', error);
      return null;
    }
  },

  // ---------------------------------------------------------------------------
  // 2. WRITE Operations (Create/Update)
  // ---------------------------------------------------------------------------

  /**
   * Check if ID/Passport Number is already in use
   */
  isIdNumberTaken: async (idNumber, excludeProfileId = null) => {
    if (!idNumber) return false;
    let query = supabase
      .from('students')
      .select('profile_id')
      .eq('id_number', idNumber);

    if (excludeProfileId) {
      query = query.neq('profile_id', excludeProfileId);
    }

    const { data, error } = await query;
    return !error && data && data.length > 0;
  },

  /**
   * Generate Admission Number
   * Format: [PROGRAM_CODE]/[LEVEL_CODE]/[SEQUENTIAL_NUMBER]/[INTAKE_CODE]
   * Level Codes: L2=200, L3=300, L4=400, L5=500, L6=600
   * Example: SLT/600/001/J26
   */
  generateAdmissionNumber: async (programId, academicLevel, intakeCode) => {
    try {
      // 1. Get Program Code
      const { data: program, error: pError } = await supabase
        .from('programs')
        .select('code')
        .eq('id', programId)
        .single();

      if (pError || !program) throw new Error('Program not found or missing code.');
      const progCode = program.code || 'GEN';

      // 2. Determine Level Code (e.g., Level 6 -> 600)
      let levelCode = '000';
      if (academicLevel) {
        const levelNumMatch = academicLevel.match(/\d+/);
        if (levelNumMatch) {
          levelCode = `${parseInt(levelNumMatch[0]) * 100}`;
        }
      }

      // 3. Find highest sequential number for this program AND level
      const { data: students, error: sError } = await supabase
        .from('students')
        .select('admission_number')
        .eq('program_id', programId)
        .ilike('admission_number', `${progCode}/${levelCode}/%`);

      let maxSeq = 0;

      if (!sError && students && students.length > 0) {
        students.forEach(s => {
          const parts = s.admission_number.split('/');
          // Format is PROG/LEVEL/SEQ/INTAKE
          if (parts.length >= 3) {
            const num = parseInt(parts[2], 10);
            if (!isNaN(num) && num > maxSeq) {
              maxSeq = num;
            }
          }
        });
      }

      const sequence = maxSeq + 1;

      // 4. Format segments
      const paddedSeq = String(sequence).padStart(3, '0');
      const formattedIntake = intakeCode?.toUpperCase();

      // Result: PROGRAM/LEVEL/SEQ/INTAKE
      return `${progCode}/${levelCode}/${paddedSeq}/${formattedIntake}`;

    } catch (error) {
      console.error('Error generating admission number:', error);
      throw error;
    }
  },

  /**
 * Create New Student
 * Note: This usually requires creating a specific User Profile first.
 * Logic:
 * 1. Check/Create User Profile (if integrated with Auth)
 * 2. Generate Admission Number
 * 3. Insert into Student Table
 */
  createStudent: async (studentData) => {
    try {
      // Input Validation
      if (!studentData.program_id || !studentData.intake_code) {
        throw new Error("Program and Intake are required.");
      }

      // 1. Uniqueness Check for ID Number
      if (studentData.id_number) {
        const isTaken = await studentService.isIdNumberTaken(studentData.id_number);
        if (isTaken) {
          throw new Error(`ID / Passport Number "${studentData.id_number}" is already registered to another student.`);
        }
      }

      // 1. Auto-generate admission number if not provided (Admin can override)
      let admissionNumber = studentData.admission_number;
      if (!admissionNumber) {
        admissionNumber = await studentService.generateAdmissionNumber(studentData.program_id, studentData.intake_code);
      }

      // 2. Prepare Profile ID
      // If studentData does not have profile_id, we create a user_profile first
      let profileId = studentData.profile_id;
      if (!profileId) {
        // Check if admin client is available
        if (!isAdminAvailable()) {
          throw new Error(
            'Student account provisioning is not available in the browser. ' +
            'Create the account through a secured server or Supabase Edge Function, then provide its profile ID.'
          );
        }

        // First, check if a user with this email already exists
        const { data: existingUsers, error: checkError } = await supabaseAdmin.auth.admin.listUsers();

        if (checkError) {
          console.error('Error checking existing users:', checkError);
        }

        const existingUser = existingUsers?.users?.find(u => u.email === studentData.email);

        if (existingUser) {
          // User already exists, use their ID
          console.log('User with this email already exists, using existing user ID');
          profileId = existingUser.id;

          // Check if profile exists, if not create it
          const { data: existingProfile } = await supabase
            .from('user_profiles')
            .select('id')
            .eq('id', profileId)
            .single();

          if (!existingProfile) {
            // Profile doesn't exist, create it
            const userProfileData = {
              id: profileId,
              email: studentData.email,
              full_name: `${studentData.first_name} ${studentData.middle_name || ''} ${studentData.last_name}`.trim(),
              primary_role: 'student',
              status: mapStudentStatusToProfileStatus(studentData.status || 'Pending')
            };

            const { error: profileError } = await supabase
              .from('user_profiles')
              .insert(userProfileData);

            if (profileError) {
              console.error('Error creating user profile for existing user:', profileError);
              throw new Error(`Failed to create user profile: ${profileError.message}`);
            }
          }
        } else {
          // Create a new authenticated user
          const tempPassword = crypto.randomUUID(); // Generate a temporary password

          const { data: authUser, error: authError } = await supabaseAdmin.auth.admin.createUser({
            email: studentData.email,
            password: tempPassword,
            email_confirm: true, // Auto-confirm email
            user_metadata: {
              full_name: `${studentData.first_name} ${studentData.middle_name || ''} ${studentData.last_name}`.trim(),
              role: 'student'
            }
          });

          if (authError) {
            console.error('Error creating auth user:', authError);
            throw new Error(`Failed to create authenticated user: ${authError.message}`);
          }

          profileId = authUser.user.id;

          // Wait a moment for any database triggers to complete
          await new Promise(resolve => setTimeout(resolve, 500));

          // Check if a profile was auto-created by a trigger
          const { data: autoProfile } = await supabase
            .from('user_profiles')
            .select('id')
            .eq('id', profileId)
            .single();

          if (!autoProfile) {
            // No auto-created profile, create it manually
            const userProfileData = {
              id: profileId,
              email: studentData.email,
              full_name: `${studentData.first_name} ${studentData.middle_name || ''} ${studentData.last_name}`.trim(),
              primary_role: 'student',
              status: mapStudentStatusToProfileStatus(studentData.status || 'Pending')
            };

            const { error: profileError } = await supabase
              .from('user_profiles')
              .insert(userProfileData);

            if (profileError) {
              console.error('Error creating user profile:', profileError);
              // Try to clean up the auth user if profile creation fails
              await supabaseAdmin.auth.admin.deleteUser(profileId);
              throw new Error(`Failed to create user profile: ${profileError.message}`);
            }
          } else {
            // Profile was auto-created, update it with correct data
            const { error: updateError } = await supabase
              .from('user_profiles')
              .update({
                full_name: `${studentData.first_name} ${studentData.middle_name || ''} ${studentData.last_name}`.trim(),
                primary_role: 'student',
                status: mapStudentStatusToProfileStatus(studentData.status || 'Pending')
              })
              .eq('id', profileId);

            if (updateError) {
              console.warn('Warning: Could not update auto-created profile:', updateError);
            }
          }
        }
      }

      // 3. Insert Student Record
      const insertPayload = {
        profile_id: profileId,
        admission_number: admissionNumber,
        first_name: studentData.first_name,
        middle_name: studentData.middle_name,
        last_name: studentData.last_name,
        email: studentData.email,
        phone_number: studentData.phone_number,
        gender: studentData.gender,
        date_of_birth: studentData.date_of_birth,
        nationality: studentData.nationality,
        id_number: studentData.id_number,
        program_id: studentData.program_id,
        academic_level: studentData.academic_level,
        // department is derived from program
        intake_code: studentData.intake_code,
        starting_module: studentData.starting_module || 1,
        current_module: studentData.starting_module || 1,
        status: studentData.status || 'Pending',
        notes: studentData.notes,
        // user_profiles is already created
      };

      const { data, error } = await supabase
        .from('students')
        .insert([insertPayload])
        .select()
        .single();

      if (error) throw error;
      return { success: true, data };

    } catch (error) {
      console.error("Create Student Error:", error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Deactivate all students whose status is 'Active' or 'Admitted'.
   * Usually triggered when a semester ends.
   */
  deactivateStudents: async () => {
    try {
      console.log('🔄 Deactivating Active and Admitted students...');
      const { data, error } = await supabase
        .from('students')
        .update({ status: 'Inactive' })
        .in('status', ['Active', 'Admitted', 'active', 'admitted', 'ACTIVE', 'ADMITTED'])
        .select('profile_id');

      if (error) throw error;
      console.log(`✅ Deactivated ${data?.length || 0} students.`);
      return { success: true, count: data?.length || 0 };
    } catch (error) {
      console.error("Deactivate Students Error:", error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Update Student
   */
  updateStudent: async (id, updates) => {
    try {
      // 1. Uniqueness Check for ID Number if it's being updated
      if (updates.id_number) {
        const isTaken = await studentService.isIdNumberTaken(updates.id_number, id);
        if (isTaken) {
          throw new Error(`ID / Passport Number "${updates.id_number}" is already registered to another student.`);
        }
      }

      const { data, error } = await supabase
        .from('students')
        .update(updates)
        .eq('profile_id', id)
        .select()
        .maybeSingle();

      if (error) throw error;
      if (!data) {
        throw new Error(
          'Student was not updated. The student may be outside your academic scope, or the scoped student-edit migration is not applied.'
        );
      }
      return { success: true, data };
    } catch (error) {
      console.error("Update Student Error:", error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Delete Student
   */
  deleteStudent: async (id) => {
    try {
      const { error } = await supabase
        .from('students')
        .delete()
        .eq('profile_id', id);

      if (error) throw error;
      return { success: true };
    } catch (error) {
      console.error("Delete Student Error:", error);
      return { success: false, error: error.message };
    }
  },

  // ---------------------------------------------------------------------------
  // 3. DOCUMENT Management
  // ---------------------------------------------------------------------------

  uploadDocument: async (studentId, file, docType) => {
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${studentId}/${Date.now()}.${fileExt}`;
      const filePath = `student-documents/${fileName}`;

      // 1. Upload to Storage
      const { error: uploadError } = await supabase.storage
        .from('documents') // Ensure this bucket exists
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      // 2. Get Public URL
      const { data: { publicUrl } } = supabase.storage
        .from('documents')
        .getPublicUrl(filePath);

      // 3. Record in DB
      const { data, error: dbError } = await supabase
        .from('student_documents')
        .insert([{
          student_id: studentId,
          document_name: file.name,
          document_type: docType,
          file_url: publicUrl
        }])
        .select()
        .single();

      if (dbError) throw dbError;
      return { success: true, data };

    } catch (error) {
      console.error("Upload Document Error:", error);
      return { success: false, error: error.message };
    }
  },

  deleteDocument: async (docId) => {
    // Logic to delete from DB and Storage
    try {
      const { error } = await supabase
        .from('student_documents')
        .delete()
        .eq('id', docId);

      if (error) throw error;
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

};

// Export individual functions for backward compatibility with Routes/other services
export const getStudents = studentService.getStudents;
export const getStudentById = studentService.getStudentById;
